import { useEffect, useState } from 'react'
import { api, ErrorDeApi, type EstadoDeSincronizacion69B, type ResumenDeSincronizacionDesdeElSat } from '../servicios/api'
import { useAvisos } from '../componentes/Toast'

/**
 * Estado y sincronización del listado 69-B del SAT (EFOS). SRS: RF-206, RF-507. Cédula: A-06.
 * El listado es nacional (aplica a todos los despachos) y el trabajo de las 04:00 solo lo
 * descarga cada semana; desde aquí un Administrador lo trae ahora mismo o carga un CSV propio.
 */
export function PanelDeListado69B() {
  const [estado, setEstado] = useState<EstadoDeSincronizacion69B | null>(null)
  const [cargado, setCargado] = useState(false)
  const [ocupado, setOcupado] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const avisar = useAvisos()

  const recargarEstado = () =>
    api
      .estadoDeLista69b()
      .then(setEstado)
      .catch((e) => setError(e instanceof ErrorDeApi ? e.message : 'No se pudo consultar el listado 69-B.'))
      .finally(() => setCargado(true))

  useEffect(() => {
    void recargarEstado()
  }, [])

  async function ejecutar(accion: () => Promise<string>) {
    setOcupado(true)
    setError(null)
    try {
      avisar(await accion())
      await recargarEstado()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo sincronizar el listado 69-B.')
    } finally {
      setOcupado(false)
    }
  }

  const sincronizarDesdeSat = () =>
    ejecutar(async () => {
      const r: ResumenDeSincronizacionDesdeElSat = await api.sincronizarLista69bDesdeSat()
      const fallas = r.fallidas.length > 0 ? ` No se pudo leer ${r.fallidas.length} de las fuentes: ${r.fallidas.map((f) => f.mensaje).join(' ')}` : ''
      return `Listado sincronizado desde el SAT: ${r.nuevos} nuevos, ${r.actualizados} actualizados, ${r.conError} renglones con error.${fallas}`
    })

  const cargarCsv = (archivo: File) =>
    ejecutar(async () => {
      const r = await api.sincronizarLista69b(archivo)
      return `CSV cargado: ${r.nuevos} nuevos, ${r.actualizados} actualizados, ${r.errores.length} renglones con error.`
    })

  return (
    <details className="tarjeta pliegue-administrativo" style={{ marginTop: 'var(--space-4)' }}>
      <summary>
        <h2 id="titulo-69b">Listado 69-B del SAT (EFOS)</h2>
        <span className="sutil">
          {!cargado
            ? 'consultando…'
            : estado
              ? `última sincronización ${new Date(estado.ultimaSincronizacionUtc).toLocaleDateString('es-MX')}`
              : 'nunca sincronizado'}
        </span>
      </summary>
      {!cargado ? (
        <p className="sutil">Consultando…</p>
      ) : estado ? (
        <p className="sutil">
          Última sincronización: {new Date(estado.ultimaSincronizacionUtc).toLocaleString('es-MX')} desde{' '}
          <span className="mono">{estado.origen}</span> · {estado.nuevos} nuevos, {estado.actualizados} actualizados,{' '}
          {estado.conError} con error.
        </p>
      ) : (
        <p className="sutil">El listado 69-B nunca se ha sincronizado: ningún cliente ni emisor se marcará en riesgo hasta entonces.</p>
      )}
      <p className="nota">
        Se actualiza solo cada semana (trabajo de las 04:00). Solo un Administrador puede sincronizarlo bajo
        demanda; el listado es nacional y aplica a todos los despachos (RF-206, RF-507).
      </p>

      <div className="acciones">
        <button className="primario" disabled={ocupado} onClick={() => void sincronizarDesdeSat()}>
          {ocupado ? 'Sincronizando…' : 'Sincronizar desde el SAT ahora'}
        </button>
        <label>
          Cargar CSV normalizado
          <input
            type="file"
            accept=".csv,text/csv"
            disabled={ocupado}
            onChange={(e) => {
              const archivo = e.target.files?.[0]
              e.target.value = ''
              if (archivo) void cargarCsv(archivo)
            }}
          />
        </label>
      </div>

      {error && <p className="error" role="alert">{error}</p>}
    </details>
  )
}
